// M16 KPI — birim çevrim tablosu + saf çevrim yardımcıları (spec K25: para birimleri çevrilmez).
// Runtime bağımlılığı yok; FE (set editörü doğrulaması) ve BE (hesaplama) aynı tabloyu kullanır.

export interface KpiUnitConversion {
  readonly from: string;
  readonly to: string;
  readonly factor: number;
}

// Tohum çiftleri. Ters yön tabloda açıkça yoksa 1/factor ile türetilir; açık tohum (GJ→kWh
// 277.778) türetilmiş tersi EZMEZ.
// 2026-10-08 (kullanıcı kararı: SI tablosu genişletildi, eş anlamlılar eklendi; yönetici
// tarafından düzenlenebilir tablo YAPILMADI — ihtiyaç çıkarsa özel birim/takma ad katmanı).
// Belirsiz kısaltmalar BİLEREK dışarıda: "ML" (megalitre ↔ mililitre), "Mt" (megaton ↔ metrik
// ton), "cal". Para birimleri K25 gereği yok.
const SEED: readonly KpiUnitConversion[] = [
  // Enerji
  { from: "kWh", to: "GJ", factor: 0.0036 },
  { from: "MWh", to: "GJ", factor: 3.6 },
  { from: "GJ", to: "kWh", factor: 277.778 },
  { from: "MWh", to: "kWh", factor: 1000 },
  { from: "GWh", to: "MWh", factor: 1000 },
  { from: "TJ", to: "GJ", factor: 1000 },
  { from: "GJ", to: "MJ", factor: 1000 },
  { from: "MJ", to: "kJ", factor: 1000 },
  { from: "kcal", to: "kJ", factor: 4.1868 },
  { from: "toe", to: "GJ", factor: 41.868 },
  { from: "MMBtu", to: "GJ", factor: 1.05506 },
  // Kütle
  { from: "ton", to: "kg", factor: 1000 },
  { from: "kg", to: "g", factor: 1000 },
  { from: "kt", to: "ton", factor: 1000 },
  // Hacim
  { from: "m3", to: "lt", factor: 1000 },
  { from: "hm3", to: "m3", factor: 1_000_000 },
  // Emisyon
  { from: "tCO2e", to: "kgCO2e", factor: 1000 },
  { from: "ktCO2e", to: "tCO2e", factor: 1000 },
  { from: "MtCO2e", to: "ktCO2e", factor: 1000 },
  // Alan
  { from: "ha", to: "m2", factor: 10_000 },
  { from: "km2", to: "ha", factor: 100 },
  // Uzunluk
  { from: "km", to: "m", factor: 1000 },
  // Zaman
  { from: "gün", to: "saat", factor: 24 },
  { from: "saat", to: "dakika", factor: 60 },
  // Güç
  { from: "MW", to: "kW", factor: 1000 },
  { from: "GW", to: "MW", factor: 1000 },
];

// Eş anlamlı birim yazımları (P3-3, gerçek LLM turu): "insan-saat"/"adam saat" → saat (20 yanlış
// "birim uyuşmazlığı" uyarısı), "yüzde"/"pct" → %, "tco2e" varyantları. Anahtar = ham normalize
// çıktısı, değer = kanonik anahtar. Para birimleri (K25) burada YOK.
const UNIT_ALIASES: ReadonlyMap<string, string> = new Map([
  ["insan-saat", "saat"],
  ["insansaat", "saat"],
  ["insan saat", "saat"],
  ["adam-saat", "saat"],
  ["adam saat", "saat"],
  ["kisi-saat", "saat"],
  ["kisi saat", "saat"],
  ["person-hour", "saat"],
  ["person-hours", "saat"],
  ["man-hour", "saat"],
  ["man-hours", "saat"],
  ["sa", "saat"],
  ["hour", "saat"],
  ["hours", "saat"],
  ["h", "saat"],
  ["yuzde", "%"],
  ["pct", "%"],
  ["percent", "%"],
  ["ton co2e", "tco2e"],
  ["ton co2", "tco2e"],
  ["tonco2e", "tco2e"],
  ["t co2e", "tco2e"],
  ["tco2", "tco2e"],
  ["metreküp", "m3"],
  ["metrekup", "m3"],
  ["litre", "lt"],
  ["l", "lt"],
  ["tonne", "ton"],
  ["tonnes", "ton"],
  ["tons", "ton"],
  // Final review I1 (2026-10-23): yüzde yazımları ("(%)" parantezi normalizeUnitKey'de soyulur),
  // para birimi simgeleri (TRY/₺ ≡ TL). Para birimleri ARASI çevrim yine yok (K25).
  ["% oran", "%"],
  ["try", "tl"],
  ["₺", "tl"],
  // P3-3 (KK/AI kontrolü ajanı): LTIF/LTIFR/TRIR "oran" (birimsiz) ailesi — "LTIF → oran"
  // yanlış birim uyuşmazlığı; "ratio" İngilizce eşdeğeri.
  ["ltif", "oran"],
  ["ltifr", "oran"],
  ["trir", "oran"],
  ["ratio", "oran"],
  ["rate", "oran"],
  // 2026-10-08 — yazım eş anlamlıları (SI genişlemesiyle)
  ["kilovatsaat", "kwh"],
  ["kilowatt saat", "kwh"],
  ["kilowatt-hour", "kwh"],
  ["kilowatt hour", "kwh"],
  ["megavatsaat", "mwh"],
  ["megawatt saat", "mwh"],
  ["megawatt-hour", "mwh"],
  ["gigavatsaat", "gwh"],
  ["gigajoule", "gj"],
  ["megajoule", "mj"],
  ["kilojoule", "kj"],
  ["kilokalori", "kcal"],
  ["tep", "toe"],
  ["ton esdeger petrol", "toe"],
  ["ton petrol esdegeri", "toe"],
  ["mmbtu", "mmbtu"],
  ["mbtu", "mmbtu"],
  ["m^3", "m3"],
  ["m^2", "m2"],
  ["metrekare", "m2"],
  ["hektar", "ha"],
  ["hectare", "ha"],
  ["kilometrekare", "km2"],
  ["km^2", "km2"],
  ["kilometre", "km"],
  ["kilometer", "km"],
  ["metre", "m"],
  ["meter", "m"],
  ["kilogram", "kg"],
  ["gram", "g"],
  ["kiloton", "kt"],
  ["bin ton", "kt"],
  ["kg co2e", "kgco2e"],
  ["kgco2", "kgco2e"],
  ["kg co2", "kgco2e"],
  ["kt co2e", "ktco2e"],
  ["ktco2", "ktco2e"],
  ["bin tco2e", "ktco2e"],
  ["bin ton co2e", "ktco2e"],
  ["mt co2e", "mtco2e"],
  ["mtco2", "mtco2e"],
  ["milyon tco2e", "mtco2e"],
  ["milyon ton co2e", "mtco2e"],
  ["day", "gun"],
  ["days", "gun"],
  ["is gunu", "gun"],
  ["dk", "dakika"],
  ["min", "dakika"],
  ["minute", "dakika"],
  ["minutes", "dakika"],
  ["megawatt", "mw"],
  ["megavat", "mw"],
  ["kilowatt", "kw"],
  ["kilovat", "kw"],
  ["gigawatt", "gw"],
]);

// Alt simge rakamlar (tCO₂e → tCO2e) — üst simge gibi düzleştirilir; NFD bunları ayrıştırmaz.
const SUBSCRIPT_DIGITS: Readonly<Record<string, string>> = {
  "₀": "0",
  "₁": "1",
  "₂": "2",
  "₃": "3",
  "₄": "4",
  "₅": "5",
  "₆": "6",
  "₇": "7",
  "₈": "8",
  "₉": "9",
};

// Karşılaştırma anahtarı: kırp, üst simge rakamları düzleştir (m³≡m3), diakritik at, küçük harf,
// eş anlamlı yazım → kanonik (insan-saat ≡ saat).
// Türkçe "İ" NFD ile "I"+nokta olur → nokta atılır → "i" (toLowerCase "ı" vermez, istenen bu).
export function normalizeUnitKey(unit: string): string {
  const key = unit
    .trim()
    .replace(/²/g, "2")
    .replace(/³/g, "3")
    .replace(/[₀-₉]/g, (d) => SUBSCRIPT_DIGITS[d] ?? d)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/^\((.*)\)$/, "$1") // "(%)" ≡ "%", "(Bin TL)" ≡ "bin tl"
    .trim();
  return UNIT_ALIASES.get(key) ?? key;
}

// ─── Aynı para biriminin ölçekleri (final review I1, 2026-10-23) ───────────────────────────
// "Bin TL" ↔ "TL" ↔ "Milyon TL" bir kur çevrimi DEĞİL, ölçektir (×1e3/×1e6/×1e9) → convertible.
// K25 "para birimi CEVIR reddi" FARKLI para birimleri arasındır (TL ↔ USD: kur tarihe bağlı, sabit
// çarpan yok) — o çift burada da null kalır. Bu çiftler SEED/komşuluk tablosuna girmez: CEVIR
// seçicisi ve "Desteklenen çevrimler" yardım tablosu para birimi listelemez (değişmedi).
const CURRENCY_BASES: ReadonlyMap<string, string> = new Map([
  ["tl", "tl"],
  ["try", "tl"],
  ["₺", "tl"],
  ["usd", "usd"],
  ["$", "usd"],
  ["eur", "eur"],
  ["€", "eur"],
  ["gbp", "gbp"],
  ["£", "gbp"],
]);
const CURRENCY_SCALES: ReadonlyMap<string, number> = new Map([
  ["bin", 1e3],
  ["thousand", 1e3],
  ["000", 1e3],
  ["milyon", 1e6],
  ["million", 1e6],
  ["mn", 1e6],
  ["milyar", 1e9],
  ["billion", 1e9],
  ["bn", 1e9],
]);

function currencyScaleOf(key: string): { base: string; scale: number } | null {
  const parts = key.split(" ");
  if (parts.length === 1) {
    const base = CURRENCY_BASES.get(parts[0]!);
    return base ? { base, scale: 1 } : null;
  }
  if (parts.length !== 2) return null;
  const scale = CURRENCY_SCALES.get(parts[0]!);
  const base = CURRENCY_BASES.get(parts[1]!);
  return scale !== undefined && base ? { base, scale } : null;
}

/** Para biriminin taban kodu ("Bin USD" → "usd", "₺" → "tl"); para birimi değilse null. */
export function currencyBaseOf(unit: string): string | null {
  return currencyScaleOf(normalizeUnitKey(unit))?.base ?? null;
}

/**
 * Para biriminin (taban ya da ölçekli) tüm ölçek yazımları — taban + "Bin/Milyon/Milyar <TABAN>"
 * (tablo sırası, her ölçek değeri için ilk Türkçe sözcük), girdinin kendisi hariç; para birimi
 * değilse []. Birim varyantı seçicisi (DR2) için: `reachableUnits` para listelemez (R5/R10).
 */
export function currencyScaleUnits(unit: string): string[] {
  const key = normalizeUnitKey(unit);
  const own = currencyScaleOf(key);
  if (!own) return [];
  const code = own.base.toUpperCase();
  const words = new Map<number, string>();
  for (const [word, scale] of CURRENCY_SCALES) {
    if (!words.has(scale))
      words.set(scale, word.charAt(0).toLocaleUpperCase("tr-TR") + word.slice(1));
  }
  const all = [code, ...[...words.values()].map((w) => `${w} ${code}`)];
  return all.filter((u) => {
    const o = currencyScaleOf(normalizeUnitKey(u));
    return !(o?.base === own.base && o.scale === own.scale);
  });
}

/** İki normalize anahtar aynı para biriminin ölçekleriyse çarpan; farklı para birimi → null;
 * biri para birimi değilse undefined (genel tabloya bakılır). */
function currencyScaleFactor(src: string, dst: string): number | null | undefined {
  const a = currencyScaleOf(src);
  const b = currencyScaleOf(dst);
  if (!a || !b) return undefined;
  return a.base === b.base ? a.scale / b.scale : null;
}

function buildTable(): readonly KpiUnitConversion[] {
  const out: KpiUnitConversion[] = [...SEED];
  const has = (from: string, to: string) =>
    out.some((c) => normalizeUnitKey(c.from) === from && normalizeUnitKey(c.to) === to);
  for (const c of SEED) {
    if (!has(normalizeUnitKey(c.to), normalizeUnitKey(c.from))) {
      out.push({ from: c.to, to: c.from, factor: 1 / c.factor });
    }
  }
  return out;
}

export const KPI_UNIT_CONVERSIONS: readonly KpiUnitConversion[] = buildTable();

// Komşuluk haritası (normalize anahtar → [hedef, çarpan]); çok adımlı çevrim için BFS tabanı.
const ADJACENCY: ReadonlyMap<string, readonly { to: string; factor: number }[]> = (() => {
  const m = new Map<string, { to: string; factor: number }[]>();
  for (const c of KPI_UNIT_CONVERSIONS) {
    const from = normalizeUnitKey(c.from);
    const list = m.get(from) ?? [];
    list.push({ to: normalizeUnitKey(c.to), factor: c.factor });
    m.set(from, list);
  }
  return m;
})();

/**
 * Birim çevrim tablosunda tanımlı mı (eş anlamlı yazımlar dahil)? "oran", "kişi" gibi birimler değil.
 *
 * KAVRAM NOTU (TODO-KPI-156): iki ayrı soru vardır, yanlış olanı seçme —
 *  - `isKnownUnit`: "bu birim ÇEVRİM yoluna girebilir mi?" (içe aktarma `planUnitConversion`,
 *    R8 "(adet) birim değil", qc-numbers, para/ölçek kararları). Sayım/oran birimleri burada false.
 *  - `isSelectableUnit`: "UI bu birimi tanıyor mu?" (birim seçici, "bilinmeyen birim" uyarısı).
 * Yeni bir tüketici çevrim/ölçek kararı veriyorsa `isKnownUnit`, kullanıcıya birim sunuyor ya da
 * doğruluyorsa `isSelectableUnit` kullanır.
 */
export function isKnownUnit(unit: string): boolean {
  const key = normalizeUnitKey(unit);
  return ADJACENCY.has(key) || currencyScaleOf(key) !== null;
}

// ---- Birim şablonları (B4): "{ciro}/kişi" — başka göstergenin birimine atıf ----
/**
 * TEK şablon jetonu kalıbı (TODO-KPI-101): `{kod}` — yakalama grubu 1 = iç metin. Global bayraklı:
 * yalnız `String.replace`/`matchAll` ile kullanın (`.test`/`.exec` lastIndex taşır).
 */
export const UNIT_TEMPLATE_TOKEN_RE = /\{([^}]*)\}/g;
const UNIT_TEMPLATE_MAX_DEPTH = 3;

export interface UnitTemplateResult {
  unit: string | null;
  unresolved: boolean;
}

export function isUnitTemplate(unit: string | null): boolean {
  return unit !== null && unit.search(UNIT_TEMPLATE_TOKEN_RE) >= 0;
}

/**
 * `{kod}` → `unitOf(kod)` (sonuç yine şablonsa özyinele, toplam derinlik ≤3); `{}`/`{_alt}` → ilk
 * kırılım çocuğunun birimi. Bilinmeyen/çözülemeyen → ham metin + `unresolved:true`. Döngü derinlik
 * sınırıyla biter.
 */
export function resolveUnitTemplate(
  template: string | null,
  unitOf: (code: string) => string | null | undefined,
  opts?: { firstChildUnit?: () => string | null; depth?: number },
): UnitTemplateResult {
  if (template === null) return { unit: null, unresolved: false };
  if (!isUnitTemplate(template)) return { unit: template, unresolved: false };
  const depth = opts?.depth ?? 0;
  if (depth >= UNIT_TEMPLATE_MAX_DEPTH) return { unit: template, unresolved: true };
  let unresolved = false;
  const out = template.replace(UNIT_TEMPLATE_TOKEN_RE, (raw, inner: string) => {
    const code = inner.trim();
    let sub: string | null | undefined;
    if (code === "" || code === "_alt") sub = opts?.firstChildUnit?.() ?? null;
    else sub = unitOf(code);
    if (sub === null || sub === undefined) {
      unresolved = true;
      return raw;
    }
    if (isUnitTemplate(sub)) {
      const r = resolveUnitTemplate(sub, unitOf, { ...opts, depth: depth + 1 });
      if (r.unresolved || r.unit === null) {
        unresolved = true;
        return raw;
      }
      return r.unit;
    }
    return sub;
  });
  return unresolved ? { unit: template, unresolved: true } : { unit: out, unresolved: false };
}

// from→to toplam çarpanı; en kısa yol (BFS, tablo sırası deterministik). Yol yoksa null.
export function unitConversionFactor(from: string, to: string): number | null {
  const src = normalizeUnitKey(from);
  const dst = normalizeUnitKey(to);
  if (src === dst) return 1;
  const currency = currencyScaleFactor(src, dst);
  if (currency !== undefined) return currency;
  const factorTo = new Map<string, number>([[src, 1]]);
  const queue: string[] = [src];
  while (queue.length > 0) {
    const cur = queue.shift();
    if (cur === undefined) break;
    const base = factorTo.get(cur) ?? 1;
    for (const edge of ADJACENCY.get(cur) ?? []) {
      if (factorTo.has(edge.to)) continue;
      const f = base * edge.factor;
      if (edge.to === dst) return f;
      factorTo.set(edge.to, f);
      queue.push(edge.to);
    }
  }
  return null;
}

// Aynı birim (normalize eşit) → değer aynen; çevrilemiyorsa null.
export function convertUnit(value: number, from: string, to: string): number | null {
  const f = unitConversionFactor(from, to);
  if (f === null) return null;
  return f === 1 ? value : value * f;
}

/**
 * Finar 14.4 (K4) — iki birimin karşılaştırılabilirliği (form sorusu ↔ KPI göstergesi bağı).
 * Birimsiz soru = bilgi yok → uyumlu (K4 kararı 2026-10-23): taraflardan biri (ya da ikisi)
 * boşsa `same` — eski framework'lerin birim beyan etmeyen soruları öneriyi kaybetmez.
 * - `same`: biri/ikisi boş ya da normalize anahtarları eşit ("%" ↔ "yüzde", "TL" ↔ "tl").
 * - `convertible`: aynı aile, farklı ölçek (kWh ↔ MWh) — çevrim tablosunda yol var; aynı para
 *   biriminin ölçekleri de (Bin TL ↔ TL ×1000 — kur değil ölçek; TL ↔ USD incompatible kalır).
 * - `incompatible`: iki taraf da birimli ve farklı aile / sayım birimi ↔ başka birim
 *   ("kişi" ↔ "%"). Çağıran bu durumda değer önermez.
 */
export function unitsCompatible(
  a: string | undefined | null,
  b: string | undefined | null,
): "same" | "convertible" | "incompatible" {
  const ka = a ? normalizeUnitKey(a) : "";
  const kb = b ? normalizeUnitKey(b) : "";
  if (ka === "" || kb === "" || ka === kb) return "same";
  return unitConversionFactor(ka, kb) !== null ? "convertible" : "incompatible";
}

const CURRENCY_KEYS: ReadonlySet<string> = new Set(
  ["TRY", "TL", "USD", "EUR", "GBP", "₺", "$", "€", "£"].map(normalizeUnitKey),
);

// K25: CEVIR para birimlerini reddeder — kur tarihe bağlıdır, sabit çarpan yoktur.
export function isCurrencyUnit(unit: string): boolean {
  return CURRENCY_KEYS.has(normalizeUnitKey(unit));
}

// ─── Birim seçici yardımcıları (CEVIR popover + "Desteklenen çevrimler" tablosu) ───────────

/** Tablodaki ilk görünen yazım (normalize anahtar → "kWh" gibi görüntü adı). */
const DISPLAY_NAME: ReadonlyMap<string, string> = (() => {
  const m = new Map<string, string>();
  for (const c of KPI_UNIT_CONVERSIONS) {
    for (const u of [c.from, c.to]) {
      const k = normalizeUnitKey(u);
      if (!m.has(k)) m.set(k, u);
    }
  }
  return m;
})();

/** Tablodaki yazımı döner ("kwh" → "kWh"); tabloda yoksa girdi kırpılmış haliyle. */
export function unitDisplayName(unit: string): string {
  return DISPLAY_NAME.get(normalizeUnitKey(unit)) ?? unit.trim();
}

/**
 * `from` biriminden tek ya da çok adımlı çevrimle ulaşılabilen hedef birimler (kendisi hariç),
 * BFS sırasıyla; görüntü adlarıyla. Tabloda olmayan birim → boş liste. Hedef birim seçicisinde
 * yalnız bunlar listelenir — "kWh → ton" gibi çevrimsiz çift seçilemez.
 */
export function reachableUnits(from: string): string[] {
  const src = normalizeUnitKey(from);
  if (!ADJACENCY.has(src)) return [];
  const seen = new Set<string>([src]);
  const queue: string[] = [src];
  const out: string[] = [];
  while (queue.length > 0) {
    const cur = queue.shift();
    if (cur === undefined) break;
    for (const edge of ADJACENCY.get(cur) ?? []) {
      if (seen.has(edge.to)) continue;
      seen.add(edge.to);
      out.push(DISPLAY_NAME.get(edge.to) ?? edge.to);
      queue.push(edge.to);
    }
  }
  return out;
}

export interface KpiUnitFamily {
  readonly key: "energy" | "mass" | "volume" | "emission" | "area" | "length" | "time" | "power";
  readonly label: string;
  /** aile üyeleri (görüntü adı, tablo sırası) */
  readonly units: readonly string[];
}

/** Çevrim tablosunun bağlı bileşenleri — yardım panelindeki gruplama. Sıra: enerji/kütle/hacim/emisyon/alan/uzunluk/zaman/güç. */
export const KPI_UNIT_FAMILIES: readonly KpiUnitFamily[] = (() => {
  const labels: Record<KpiUnitFamily["key"], string> = {
    energy: "Enerji",
    mass: "Kütle",
    volume: "Hacim",
    emission: "Emisyon",
    area: "Alan",
    length: "Uzunluk",
    time: "Zaman",
    power: "Güç",
  };
  const seedOf: Record<KpiUnitFamily["key"], string> = {
    energy: "kWh",
    mass: "ton",
    volume: "m3",
    emission: "tCO2e",
    area: "m2",
    length: "km",
    time: "saat",
    power: "MW",
  };
  return (Object.keys(labels) as KpiUnitFamily["key"][]).map((key) => ({
    key,
    label: labels[key],
    units: [unitDisplayName(seedOf[key]), ...reachableUnits(seedOf[key])],
  }));
})();

/** Birimin ailesi (energy/mass/volume/emission/area/length/time/power); tabloda yoksa null. */
export function unitFamilyOf(unit: string): KpiUnitFamily["key"] | null {
  const k = normalizeUnitKey(unit);
  for (const fam of KPI_UNIT_FAMILIES) {
    if (fam.units.some((u) => normalizeUnitKey(u) === k)) return fam.key;
  }
  return null;
}

// ─── Sayım / oran birimleri (Task 8, 2026-11-02) ───────────────────────────────────────────
// "kişi/adet/kez/puan/oran/%" çevrim tablosunda YOK ve olmayacak (aralarında çevrim anlamsız) —
// ama geçerli birimlerdir: seçicide listelenir, "bilinmeyen birim" uyarısı almaz. `isKnownUnit`
// bilerek DEĞİŞMEDİ (içe aktarma `planUnitConversion`/R8 "(adet) birim değil"/qc-numbers onu
// çevrim-tablosu anlamında kullanır); UI "tanınan birim mi?" sorusu için `isSelectableUnit`.
export const KPI_COUNT_UNITS: readonly string[] = ["adet", "kişi", "kez", "puan", "oran", "%"];
const COUNT_UNIT_KEYS: ReadonlySet<string> = new Set(KPI_COUNT_UNITS.map(normalizeUnitKey));

/** Sayım / oran ailesinden mi (adet, kişi, kez, puan, oran, % ve eş anlamlıları)? */
export function isCountUnit(unit: string): boolean {
  return COUNT_UNIT_KEYS.has(normalizeUnitKey(unit));
}

/**
 * Seçicide tanınan birim: çevrim tablosu ∨ para (ölçekli dahil) ∨ sayım/oran. UI sorusudur —
 * çevrim yolu için `isKnownUnit` (kavram notu yukarıda).
 */
export function isSelectableUnit(unit: string): boolean {
  return unit.trim() !== "" && (isKnownUnit(unit) || isCountUnit(unit));
}

export interface KpiUnitGroup {
  readonly key: KpiUnitFamily["key"] | "count";
  readonly label: string;
  readonly units: readonly string[];
}

/** Birim seçici grupları: çevrim aileleri (`KPI_UNIT_FAMILIES`) + sonda çevrimsiz "Sayım / oran". */
export const KPI_UNIT_GROUPS: readonly KpiUnitGroup[] = [
  ...KPI_UNIT_FAMILIES,
  { key: "count", label: "Sayım / oran", units: KPI_COUNT_UNITS },
];

/** Birimin seçici grubu (çevrim ailesi ∨ "count"); tanınmayan / para → null. */
export function unitGroupKeyOf(unit: string): KpiUnitGroup["key"] | null {
  return unitFamilyOf(unit) ?? (isCountUnit(unit) ? "count" : null);
}
